/* eslint-disable tailwindcss/no-custom-classname */

/* eslint-disable jsx-a11y/anchor-is-valid */

/* eslint-disable unicorn/prevent-abbreviations */
import { broker } from '#~/agent/broker.js';
import { ApiError } from '#~/api/types.js';
import { toIdFromHex } from '#~/api/utils.js';
import { EMAIL_VERIFICATION_RESEND_COOLDOWN_MS } from '#~/auth/email-verification-gate/constants.js';
import { readClaims, remainingCooldownMs } from '#~/auth/email-verification-gate/logic.js';
import { useAuth0 } from '#~/auth/index.js';
import { getVerificationEmailSentAt, resendVerificationEmail } from '#~/auth/utils.js';
import { useDepot } from '#~/depot/hooks.js';
import { ActionBox, ButtonBox, Heading } from '#~/account/components/settings/layout/index.js';
import { useWiring } from '#~/account/components/settings/wiring.js';
import { Sel, STagNew } from '#~/stag/index.js';
import type { Props } from './types.js';
import { Avatar } from '@studio/design-system/avatar';
import { Anchor, Button } from '@studio/design-system/button';
import { Footer } from '@studio/design-system/dialog';
import { Group } from '@studio/design-system/form';
import { Icon } from '@studio/design-system/icon';
import { Input } from '@studio/design-system/input';
import { Prompt } from '@studio/design-system/prompt';
import type { User } from '@studio/models/user';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import React, { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';

const omitEmpty = (value: T_Maybe<string>): T_Maybe<string> => {
  if (!value || value.trim().length === 0) {
    return undefined;
  }

  return value;
};

// The server's 60 s resend window starts at a stamp stored to whole seconds (up to
// 0.5 s late), so one extra second keeps the button from re-enabling into a 429.
const VERIFICATION_RESEND_HOLD_MS = EMAIL_VERIFICATION_RESEND_COOLDOWN_MS + 1_000;

type VerificationSendFailure = 'failed' | 'throttled';

const latestTime = (a: T_Maybe<number>, b: T_Maybe<number>): T_Maybe<number> =>
  a === undefined ? b : b === undefined ? a : Math.max(a, b);

/** `<Profile>` panel. */
export const Profile = ({ setFooter, ...props }: Props): ReactElement => {
  const [dirtyKeys, setDirtyKeys] = useState<string[]>([]);
  const wiring = useWiring();

  const { getAccessTokenSilently, logout, user: auth0User } = useAuth0();

  const user = useDepot((µ) => µ.user)!;
  // Sends are recorded under the address Auth0 mails (the gate records them the same
  // way), so a send made elsewhere in this page shows here.
  const verificationAddress = readClaims(auth0User).email ?? user.email;
  const currentName = user.firstName + (user.lastName ? ` ${user.lastName}` : '');

  const [name, setName] = useState(currentName);
  const [emailVerified, setEmailVerified] = useState(!!user.emailVerified);
  const [email, setEmail] = useState(user.email);
  const [profilePhotoURL, setProfilePhotoURL] = useState(user.profilePhotoUrl);

  const [isShowingDeleteAccountPrompt, setIsShowingDeleteAccountPrompt] = useState(false);
  const [sendingVerificationEmail, setSendingVerificationEmail] = useState(false);
  const [verificationFailure, setVerificationFailure] = useState<VerificationSendFailure>();
  // Hold start on the monotonic page clock, so system clock changes cannot skew it.
  const [verificationHoldFrom, setVerificationHoldFrom] = useState<number>();
  const [verificationClock, setVerificationClock] = useState(() => performance.now());

  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [agents] = useDepot((µ) => [µ.agents]);

  const handleCommit = useLatestFn(
    async (overrides: T_Optional<User & { fullName: string }> = {}): Promise<void> => {
      setIsSaving(true);

      const emailChanged = (overrides.email ?? email) !== user.email;

      await wiring.handleUpdateUser({
        email: omitEmpty(overrides.email ?? email),
        fullName: omitEmpty(overrides.fullName ?? name),
        profilePhotoUrl: overrides.profilePhotoUrl ?? profilePhotoURL
      });

      setIsSaving(false);

      if (emailChanged) {
        setEmailVerified(false);
        logout();
      }

      setDirtyKeys([]);

      for (const agent of Object.values(agents)) {
        const proxy = broker.acquire(agent.id);

        if (proxy) {
          proxy.forcePing();
        }
      }
    }
  );

  const handleDirtyCheck =
    (key: string, current: T_Maybe<string>, setter: (next: string) => void) =>
    (next: string): void => {
      setter(next);

      if (next === current) {
        setDirtyKeys((keys: string[]) => keys.filter((k) => k !== key));

        return;
      }

      setDirtyKeys((keys: string[]) => {
        if (keys.includes(key)) {
          return keys;
        }

        return [...keys, key];
      });
    };

  const handleDeleteAccount = useLatestFn((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    setIsShowingDeleteAccountPrompt(true);
  });

  const handleConfirmDeleteAccount = useLatestFn(() => {
    setIsDeleting(true);
    wiring.handleDeleteUser(user);
  });

  const uploadPhoto = useLatestFn((): void => {
    // click the hidden input to trigger the file upload dialog
    fileInputRef.current?.click();

    // listen for the file upload dialog to close
    fileInputRef.current?.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];

      if (!file) {
        return;
      }

      setIsUploading(true);

      const url = await wiring.handleUploadProfilePhoto({
        photo: file
      });

      setProfilePhotoURL(url);

      await handleCommit({ profilePhotoUrl: url });

      setIsUploading(false);

      fileInputRef.current!.value = '';
    });
  });

  const removePhoto = useLatestFn(async (): Promise<void> => {
    setIsRemoving(true);
    await wiring.handleDestroyProfilePhoto();

    setProfilePhotoURL('');

    await handleCommit({ profilePhotoUrl: '' });
    setIsRemoving(false);
  });

  // Read on every render: a send that settled while the panel was closed shows on reopen.
  const verificationSent = getVerificationEmailSentAt(verificationAddress);

  const verificationRemainingMs = (now: number): number =>
    remainingCooldownMs(
      latestTime(getVerificationEmailSentAt(verificationAddress)?.mono, verificationHoldFrom),
      now,
      VERIFICATION_RESEND_HOLD_MS
    );

  const verificationCoolingDown = verificationRemainingMs(verificationClock) > 0;
  const verificationSeconds = Math.ceil(verificationRemainingMs(verificationClock) / 1000);

  useEffect(() => {
    if (!verificationCoolingDown) {
      return undefined;
    }

    const interval = setInterval(() => setVerificationClock(performance.now()), 1_000);

    return () => clearInterval(interval);
  }, [verificationCoolingDown]);

  const handleSendVerificationEmail = useLatestFn(async (): Promise<void> => {
    // A same-tick second click merges into the pending request in the shared helper.
    if (sendingVerificationEmail) {
      return;
    }

    const clickedAt = performance.now();

    if (verificationRemainingMs(clickedAt) > 0) {
      // A send recorded elsewhere since the last render: show its countdown instead.
      setVerificationClock(clickedAt);
      return;
    }

    setSendingVerificationEmail(true);
    setVerificationFailure(undefined);

    try {
      const token = await getAccessTokenSilently();
      const id = toIdFromHex(user.id);

      if (!id) {
        throw new Error('Verification email destination unavailable');
      }

      await resendVerificationEmail({ id, token, email: verificationAddress });
      setVerificationHoldFrom(
        getVerificationEmailSentAt(verificationAddress)?.mono ?? performance.now()
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 429) {
        // The server does not share the earlier send's time, so hold without a number.
        setVerificationFailure('throttled');
        setVerificationHoldFrom(performance.now());
      } else {
        // Same wording as the per-action gate's failure line (use-verified-action.tsx).
        setVerificationFailure('failed');
      }
    } finally {
      // Per rule: settle-every-terminal-branch (ai.knowledge/global) — every outcome releases Send.
      setSendingVerificationEmail(false);
      setVerificationClock(performance.now());
    }
  });

  // One sentence and one inline link on a single line; "has been sent" shows only
  // after an accepted send.
  let verificationMessage = "Your email address isn't verified yet.";

  if (verificationFailure === 'failed') {
    verificationMessage = "Couldn't send the verification email.";
  } else if (verificationFailure === 'throttled' && verificationCoolingDown) {
    verificationMessage = 'A verification email was sent recently. Try again in a minute.';
  } else if (verificationSent) {
    verificationMessage = 'A new verification email has been sent.';
  }

  let verificationLabel = 'Send verification email';

  if (sendingVerificationEmail) {
    verificationLabel = 'Sending…';
  } else if (verificationCoolingDown && verificationFailure !== 'throttled') {
    verificationLabel = `Send Again in ${verificationSeconds}s`;
  } else if (verificationSent || verificationFailure) {
    verificationLabel = 'Send Again';
  }

  useEffect(() => {
    setFooter(
      <Footer>
        <ButtonBox className='flex flex-1'>
          <ButtonBox.Cancel
            onClick={wiring.handleClose}
            {...STagNew(Sel.AccountSettings, 'cancel')}
          />
          <div className='flex-1' />
          <ButtonBox.Save
            disabled={dirtyKeys.length === 0 || isSaving}
            label={isSaving ? 'Saving...' : 'Save Changes'}
            onClick={(): void => {
              void handleCommit();
            }}
          />
        </ButtonBox>
      </Footer>
    );
  }, [dirtyKeys, handleCommit, isSaving, setFooter, wiring]);

  return (
    <div {...props}>
      <ActionBox>
        <Heading
          subtitle='Update your photo and personal details here.'
          title='Profile info'
          {...STagNew(Sel.AccountSettings, 'profile-heading')}
        />
      </ActionBox>

      <div className='w-[50%]'>
        <div className='my-32'>
          <div className='mb-32 flex flex-row'>
            <div className='mr-24 min-w-[80px]'>
              <Avatar
                key={`avatar-${name}-${profilePhotoURL ?? ''}`}
                name={name}
                photo={profilePhotoURL}
                size='lg'
              />
            </div>
            <div>
              <div className='mb-8'>
                <h2 className='m-0 p-0 font-demibold'>Upload a profile photo</h2>
                <p className='m-0 p-0 text-[12px] text-secondary'>PNG or JPG (max size: 3MB)</p>
              </div>

              <div>
                <div className='h-[1px] w-[1px] overflow-hidden opacity-0'>
                  <input accept='image/*' id='file' ref={fileInputRef} type='file' />
                </div>
                <div className='flex flex-row'>
                  <Button
                    className='mr-[8px] border-white text-white'
                    disabled={isUploading || isRemoving}
                    key={`upload-photo-${profilePhotoURL ?? ''}-${isRemoving ? 'removing' : ''}-${
                      isUploading ? 'uploading' : ''
                    }`}
                    onClick={(): void => {
                      uploadPhoto();
                    }}
                    variant='tertiary'
                  >
                    {isUploading
                      ? 'Uploading Photo...'
                      : `${profilePhotoURL ? 'Update' : 'Upload'} Photo`}
                  </Button>
                  {!!profilePhotoURL && (
                    <Button
                      className='mr-[8px] border-white text-white'
                      disabled={isRemoving || isUploading}
                      key={`remove-photo-${profilePhotoURL}-${isRemoving ? 'removing' : ''}-${
                        isUploading ? 'uploading' : ''
                      }`}
                      onClick={(): void => {
                        void removePhoto();
                      }}
                      variant='tertiary'
                    >
                      {isRemoving ? 'Removing Photo...' : 'Remove Photo'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <hr />
        </div>

        <div className='my-16 flex flex-col gap-32'>
          <Group label='Full Name'>
            <Input
              onChange={handleDirtyCheck('name', currentName, setName)}
              placeholder='Full Name'
              value={name}
            />
          </Group>

          <Group label='Email'>
            <Input
              className={twMerge('mb-8', !emailVerified && 'border border-warning')}
              onChange={handleDirtyCheck('email', user.email, setEmail)}
              placeholder='Email'
              value={email}
            />

            {!emailVerified && (
              <div className='text-sm text-warning'>
                <div className='mb-16 align-middle'>
                  <Icon icon='info' />
                  <span className='pl-4 text-[10px]'>Email Unverified</span>
                </div>

                <div>
                  <span role='status'>{verificationMessage}</span>{' '}
                  <Anchor
                    className='pl-4 text-white'
                    disabled={sendingVerificationEmail || verificationCoolingDown}
                    onClick={(): void => {
                      void handleSendVerificationEmail();
                    }}
                    {...STagNew(Sel.AccountSettings, 'send-verification-email')}
                  >
                    {verificationLabel}
                  </Anchor>
                </div>
              </div>
            )}
          </Group>

          <a className={twMerge(isDeleting && 'text-muted')} href='#' onClick={handleDeleteAccount}>
            {isDeleting ? 'Deleting' : 'Delete'} Account
          </a>

          {!!isShowingDeleteAccountPrompt && (
            <Prompt
              className='min-w-[450px]'
              message={
                <div className='text-[14px] font-[400] text-secondary'>
                  <p>Are you sure you want to delete your account?</p>

                  <div className='mx-auto mt-40 flex items-center justify-center gap-12'>
                    <Button
                      className='border-base-800 text-base-800 border-2 bg-transparent hover:border-dark hover:enabled:bg-dark hover:enabled:text-white'
                      onClick={(): void => {
                        setIsShowingDeleteAccountPrompt(false);
                      }}
                      size='lg'
                    >
                      Cancel
                    </Button>

                    <Button className='bg-error' onClick={handleConfirmDeleteAccount} size='lg'>
                      Delete Account
                    </Button>
                  </div>
                </div>
              }
              onOpenChange={(next): void => {
                if (!next) {
                  setIsShowingDeleteAccountPrompt(false);
                }
              }}
              title={<h1 className='mb-0 text-[24px] font-demibold'>Delete my account</h1>}
            />
          )}
        </div>
      </div>
    </div>
  );
};
